import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import ProjectCard from "@/components/site/ProjectCard";
import { PageFade, EASE } from "@/components/site/motion";
import { CATEGORIES } from "@/lib/constants";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const SPANS = ["lg:col-span-6", "lg:col-span-6", "lg:col-span-6", "lg:col-span-6", "lg:col-span-6", "lg:col-span-6", "lg:col-span-6"];
const ASPECTS = ["aspect-video", "aspect-video", "aspect-video", "aspect-video", "aspect-video", "aspect-video", "aspect-video"];

export default function Work() {
  useDocumentTitle("Work — JARE");
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/portfolio")
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      selected.length === 0
        ? projects
        : projects.filter((p) => selected.some((c) => p.category === c || p.categories?.includes(c))),
    [projects, selected]
  );

  return (
    <PageFade className="mx-auto w-[92%] max-w-7xl pt-40 pb-28">
      <p className="eyebrow mb-6">Portfolio</p>
      <h1 className="font-display text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="work-page-title">
        Selected Work
      </h1>
      <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">
        Identity systems, motion languages, films and interfaces — a cross-disciplinary
        archive of client work and experiments.
      </p>

      <div className="mt-12 flex flex-wrap items-center gap-2.5" data-testid="portfolio-filters">
        {CATEGORIES.map((c) => {
          const isAll = c === "All";
          const isActive = isAll ? selected.length === 0 : selected.includes(c);
          return (
            <button
              key={c}
              onClick={() => {
                if (isAll) {
                  setSelected([]);
                  return;
                }
                setSelected((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
              }}
              data-testid={`filter-button-${c.toLowerCase().replace(/[\s/]+/g, "-")}`}
              className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                isActive
                  ? "border-white bg-white text-black"
                  : "border-white/12 bg-white/[0.03] text-neutral-400 hover:border-white/30 hover:text-white"
              }`}
            >
              {c}
            </button>
          );
        })}
        {selected.length > 0 && (
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500" data-testid="filter-active-count">
            {selected.length} selected
          </span>
        )}
      </div>

      <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-600" data-testid="portfolio-count">
        {filtered.length} project{filtered.length === 1 ? "" : "s"}
      </p>

      {loading ? (
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${SPANS[i]} col-span-1 md:col-span-1`}>
              <div className="aspect-video animate-pulse rounded-2xl bg-white/[0.04]" />
            </div>
          ))}
        </div>
      ) : (
        <motion.div layout className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 lg:gap-8" data-testid="portfolio-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: EASE }}
                className={`col-span-1 md:col-span-1 ${SPANS[i % SPANS.length]}`}
              >
                <ProjectCard project={p} aspect={ASPECTS[i % ASPECTS.length]} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {!loading && filtered.length === 0 && (
        <p className="mt-20 text-center text-neutral-500" data-testid="portfolio-empty">
          No published projects in this category yet.
        </p>
      )}
    </PageFade>
  );
}
